import { Button } from 'antd'
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as Logo } from '../assets/images/logo.svg'
import PWAInstallButton from '../components/PWAInstallButton'
import { usePWAContext } from '../contexts/PWAContext'

const DownloadAppPage: React.FC = () => {
	const navigate = useNavigate()
	const { isInstallable, isInstalled, isReady, browserCategory, openInstalledApp } = usePWAContext()

	// Enhanced standalone mode detection with multiple checks
	const getStandaloneStatus = () => {
		const windowStandalone = (window as any).navigator?.standalone === true
		const displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches
		const displayModeFullscreen = window.matchMedia('(display-mode: fullscreen)').matches
		const displayModeMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches

		// Multiple detection methods for better reliability
		const isStandalone = windowStandalone || displayModeStandalone || displayModeFullscreen || displayModeMinimalUI

		return {
			isStandalone,
			windowStandalone,
			displayModeStandalone,
			displayModeFullscreen,
			displayModeMinimalUI,
			userAgent: navigator.userAgent,
			referrer: document.referrer
		}
	}

	const standaloneStatus = getStandaloneStatus()

	useEffect(() => {
		document.title = 'Download WageNow App - WageNow'
	}, [])

	const handleGoToLogin = () => {
		// Wait for PWA context to be ready before making navigation decisions
		if (!isReady) {
			setTimeout(() => handleGoToLogin(), 100)
			return
		}

		// Use enhanced standalone detection instead of helper function
		const currentStandaloneStatus = getStandaloneStatus()
		const inStandaloneMode = currentStandaloneStatus.isStandalone

		if (inStandaloneMode) {
			// User is already in the installed PWA - navigate within the same context
			navigate('/login')
		} else if (isInstalled) {
			// User is in browser but app is installed - open the PWA app
			openInstalledApp()
		} else {
			// Normal browser navigation for non-installed app
			navigate('/login')
		}
	}

	const handleInstallSuccess = () => {
		// After successful installation, the app should be in standalone mode
		// Force a re-check of standalone status and update navigation logic
		setTimeout(() => {
			const newStandaloneStatus = getStandaloneStatus()
			if (newStandaloneStatus.isStandalone) {
				// App is now in standalone mode, navigate within PWA context
				navigate('/login')
			}
		}, 1000) // Small delay to allow PWA to fully initialize
	}

	return (
		<div className='min-h-screen bg-gradient-to-br from-purple-50 to-white flex flex-col items-center justify-center p-4 space-y-6'>
			<div className='max-w-md w-full text-center space-y-6'>
				{/* Logo/Header */}
				<div className='mb-10'>
					<Logo className='h-12 mx-auto' />
				</div>

				{/* Unified PWA Installation Card */}
				<div className='bg-white rounded-lg shadow-lg border border-gray-200 p-6 space-y-6'>
					<div className='text-center'>
						<h2 className='text-2xl font-bold text-gray-900 mb-2'>📱 Install WageNow as an App</h2>
						{isInstalled ? (
							<div className='space-y-3'>
								<h3 className='text-xl font-bold text-green-900'>✅ App Installed!</h3>
								<p className='text-green-700'>Your WageNow app is ready to use. Click below to open it.</p>
							</div>
						) : (
							<p className='text-gray-600'>
								{browserCategory === 'supported-automatic'
									? 'Get the best experience with our native app'
									: browserCategory === 'supported-manual'
										? 'Install WageNow for the best mobile experience'
										: 'For the best experience, use a supported browser'}
							</p>
						)}
					</div>

					{/* Chrome/Edge: Automatic Install Button + Manual Instructions */}
					{!isInstalled && browserCategory === 'supported-automatic' && (
						<div className='space-y-4'>
							{/* Automatic Install Button */}
							{isInstallable && (
								<div className='bg-purple-50 border border-purple-200 rounded-lg p-4'>
									<h3 className='font-semibold text-purple-900 mb-2'>🚀 Quick Install</h3>
									<p className='text-purple-700 text-sm mb-3'>Click the button below to install WageNow as a native app</p>
									<PWAInstallButton onInstallSuccess={handleInstallSuccess} />
								</div>
							)}

							{/* Manual Instructions as Fallback */}
							<div className='bg-gray-50 border border-gray-200 rounded-lg p-4'>
								<h3 className='font-semibold text-gray-900 mb-2'>📋 Manual Installation</h3>
								<p className='text-gray-700 text-sm mb-2'>If the automatic install doesn&#39;t work, follow these steps:</p>
								<ul className='text-sm text-gray-600 space-y-1'>
									<li>• Look for the install icon in your browser address bar</li>
									<li>• Or check your browser menu for install options</li>
									<li>• Click &#34;Install&#34; to add WageNow to your applications</li>
								</ul>
							</div>
						</div>
					)}

					{/* Safari: Manual Instructions Only */}
					{!isInstalled && browserCategory === 'supported-manual' && (
						<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
							<h3 className='font-semibold text-blue-900 mb-2'>🍎 Safari Installation</h3>
							<p className='text-blue-700 text-sm mb-2'>Follow these steps to install WageNow:</p>
							<ul className='text-sm text-blue-600 space-y-1'>
								<li>• Tap the Share button (square with arrow)</li>
								<li>• Scroll down and tap &#34;Add to Home Screen&#34;</li>
								<li>• Tap &#34;Add&#34; to install the app</li>
							</ul>
						</div>
					)}

					{/* Unsupported Browsers: Recommendation */}
					{!isInstalled && browserCategory === 'unsupported' && (
						<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
							<h3 className='font-semibold text-yellow-900 mb-2'>⚠️ Browser Not Supported</h3>
							<p className='text-yellow-700 text-sm mb-3'>
								Your current browser doesn&#39;t support reliable PWA installation. For the best experience, please use:
							</p>
							<ul className='text-sm text-yellow-600 space-y-1 mb-3'>
								<li>
									• <strong>Chrome</strong> - Full automatic installation support
								</li>
								<li>
									• <strong>Microsoft Edge</strong> - Full automatic installation support
								</li>
								<li>
									• <strong>Safari</strong> - Manual installation support
								</li>
							</ul>
							<p className='text-yellow-700 text-sm'>
								You can still use WageNow in your browser, but the app experience is only available in supported browsers.
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Action Button */}
			<div className='space-y-4 pt-5'>
				<p className='text-sm text-gray-500'>
					{standaloneStatus.isStandalone
						? "You're using the WageNow app! Ready to get started?"
						: isInstalled
							? 'App installed successfully! Ready to use WageNow?'
							: "Don't want the app right now? Go to login."}
				</p>
				<Button type='primary' shape='round' size='middle' block onClick={handleGoToLogin}>
					{standaloneStatus.isStandalone ? 'Continue to Login' : isInstalled ? 'Open WageNow App' : 'Go to Login'}
				</Button>
			</div>
		</div>
	)
}

export default DownloadAppPage
